import { lazy, useCallback, useMemo, useState, Suspense } from 'react';
import HeaderWithSkeleton from '@picsart/growth-rc/HeaderWithSkeleton';
import PromoTopBanner from '@picsart/monetization/PromoTopBanner';
import useStorageData from '@picsart/monetization/useStorageData';
import useCreditsInfo from '@picsart/monetization/useCreditsInfo';
import Loading from '@picsart/rc/old/Loading';

import { HEADER_MONETIZATION_BUTTON, REGISTRATION_POINT } from 'constants/homePage';
import MockHeader from 'components/Header/Mockheader';
import type { HeaderType } from 'components/Header/types';
import { checkSpecialMarketingPage } from 'constants/main';
import useScreenName from 'hooks/useScreenName';
import useMonetizationDataStore from 'stores/monetizationDataStore';
import useLandingPropsStore from 'stores/landingPropsStore';
import { setStorage } from 'utils/crudStorage';
import usePageSlug from 'hooks/usePageSlug';
import { usePulseSettings } from '@pulse/react';
import { LandingCategory } from 'constants/enums';

const AuthModal = lazy(() => import(/* webpackChunkName: "AuthModal" */ 'components/AuthModal'));

function Header({
  onClick,
  miniNavigation,
  isLandingOrHub,
  hideHeaderAndFooterNavigations,
  logoBehavior,
}: HeaderType) {
  const screenName = useScreenName();
  const { languageParam, slug } = usePageSlug();
  const isHomePage = `/${slug}` === '/';

  const setIsClickedFromSpecialOffer = useMonetizationDataStore(
    x => x.setIsClickedFromSpecialOffer,
  );

  const checkoutGtmData = useMemo(
    () => ({ source: `landing_${screenName.replace(' ', '_').toLowerCase()}` }),
    [screenName],
  );

  const [isPromoBannerVisible, setIsPromoBannerVisible] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const creditsData = useCreditsInfo({ checkoutGtmData });
  const storageData = useStorageData({ checkoutGtmData });

  const category = useLandingPropsStore(x => x.category);
  const isPartnership = checkSpecialMarketingPage(screenName) || category === 'partnership';

  const closeAuthModal = useCallback(() => {
    setIsClickedFromSpecialOffer(false);
    setShowAuthModal(false);
  }, [setIsClickedFromSpecialOffer]);

  // const handlePromoOfferClick = useCallback(() => {
  //   setIsClickedFromSpecialOffer(true);
  //   setShowAuthModal(true);
  // }, [setIsClickedFromSpecialOffer]);

  const goToNextUrl = useCallback(() => {
    if (isHomePage) {
      setShowAuthModal(false);
      const lang = languageParam ? `/${languageParam}` : '';
      setStorage({ name: REGISTRATION_POINT.COOKIE_NAME, value: REGISTRATION_POINT.HOME });
      setStorage({ name: HEADER_MONETIZATION_BUTTON, value: 'true' });
      window.location.href = `${lang}/create?page_origin=homepage&action_button=top_banner`;
    }
  }, [languageParam, isHomePage]);

  const { settings: hideSmartBanner, loading } = usePulseSettings<boolean>({
    name: 'hide_smart_banner',
    tag: 'hide_smart_banner',
  });

  const { settings: promoBannerProps } = usePulseSettings<{
    hideBanner: boolean;
    text: string;
    buttonText: string;
    buttonLink: string;
    mobileText: string;
  }>({
    name: 'promo_banner',
    tag: 'promo_banner',
  });

  if (miniNavigation) {
    return <MockHeader onClick={onClick} />;
  }

  return (
    <>
      {isPromoBannerVisible && isLandingOrHub && category !== LandingCategory.maximizer && (
        <PromoTopBanner
          /* TODO: temporary commented so the monetizations handler will run, please check if this should be passed or not */
          // onButtonClick={isHomePage ? handlePromoOfferClick : undefined}
          handleBannerClose={() => setIsPromoBannerVisible(false)}
          checkoutGtmData={checkoutGtmData}
        />
      )}
      <HeaderWithSkeleton
        applicationName="landing"
        hideAppBanner={!loading && hideSmartBanner}
        creditsData={creditsData}
        hideNavigation={hideHeaderAndFooterNavigations}
        promoBannerProps={promoBannerProps}
        storageData={{
          ...storageData,
          hideStorage:
            isPartnership || !!(storageData.userType === 'pro' || storageData.userType === 'plus'),
        }}
        hideSearchButton={hideHeaderAndFooterNavigations}
        showProfileFeed={category !== LandingCategory.maximizer}
        logoBehavior={logoBehavior}
        isMaximizer={category === LandingCategory.maximizer || isHomePage}
      />

      {showAuthModal && (
        <Suspense fallback={<Loading />}>
          <AuthModal
            isOpen={showAuthModal}
            closeHandler={closeAuthModal}
            successHandler={goToNextUrl}
          />
        </Suspense>
      )}
    </>
  );
}

export default Header;
